import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ScreenshotIcon from '@mui/icons-material/ScreenshotMonitor';
import SendIcon from '@mui/icons-material/Send';
import { PendingAttachmentChips } from 'src/components/SupportCase/AttachmentChips';
import useAttachmentUploads from 'src/components/SupportCase/useAttachmentUploads';
import { useScreenCapture } from 'src/hooks/useScreenCapture';
import {
  ATTACHMENT_ACCEPT,
  MAX_FILES_PER_MESSAGE,
} from 'src/constants/supportCaseAttachments';
import type { AgentHealth } from 'src/components/SupportCase/types';
import { PANEL_TOOLTIP_SLOT_PROPS } from 'src/components/SupportCase/panelTooltip';

interface ComposerProps {
  onSend: (
    message: string,
    clientMsgId: string,
    attachmentIds: number[]
  ) => Promise<boolean>;
  /** Disabled while the agent is working; the user gets the floor back after. */
  disabled?: boolean;
  health: AgentHealth | null;
  healthLoading?: boolean;
  /** False when the case is resolved and the server would refuse a file. */
  allowAttachments?: boolean;
  /**
   * What the field says when it is empty (ENG-4220).
   *
   * The old copy said "Ask about this certification…" everywhere, including the
   * property pages and the hub where there is no certification. The default now says
   * what the thing is; a caller with a certification in hand may be more specific.
   */
  placeholder?: string;
  /** Rendered under the action row — the saved-prompt chips and the save nudge. */
  footer?: ReactNode;
}

const MAX_LENGTH = 4000;

/**
 * The case composer (ENG-4137, attachments ENG-4185).
 *
 * Three states, and the difference is the point:
 *
 * * **ready** — the agent is reachable and the case accepts a message;
 * * **working** — the agent is mid-turn (`agent_active`), so sending is disabled and
 *   the field says why rather than silently swallowing a message;
 * * **unavailable** — Agnus cannot be reached. Decision 2A: there is **no
 *   Thor-native fallback**, so the composer is disabled and the message names the
 *   human address instead. A half-working fallback would answer in a different voice
 *   and, worse, without the agent's guardrails.
 *
 * A file is accepted three ways and they all end in the same chip strip: the paperclip
 * (a picker filtered to the allowlist), the screenshot button (the browser's own
 * share picker, so the user chooses what to capture), and paste. Paste was the one
 * people asked for: a screenshot on the clipboard is the fastest way to show what
 * went wrong, and requiring a save-then-choose round trip is how a bug report
 * degrades into a description of a bug.
 *
 * Enter sends, Shift+Enter breaks the line. The `client_msg_id` is minted once per
 * send attempt and reused on retry, so a flaky network cannot double-post. Send waits
 * for every attached file to be uploaded, because the turn has to name ids that exist.
 */
const Composer: FC<ComposerProps> = ({
  onSend,
  disabled = false,
  health,
  healthLoading = false,
  allowAttachments = true,
  placeholder: basePlaceholder = 'Ask the Pronto Assistant…',
  footer,
}) => {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingIdRef = useRef<string>('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    attachments,
    addFiles,
    remove,
    reset,
    readyIds,
    uploading,
    notice,
    clearNotice,
  } = useAttachmentUploads();
  const {
    capture,
    capturing,
    error: captureError,
    notice: captureNotice,
    clearError,
    // `clearNotice` is already taken by the upload queue's own notice.
    clearNotice: clearCaptureNotice,
  } = useScreenCapture();

  const unavailable = health !== null && !health.available;
  const blocked = disabled || unavailable || sending || uploading;
  const canSend = !blocked && value.trim().length > 0;

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  const submit = useCallback(async () => {
    const message = value.trim();
    if (!message || uploading) {
      return;
    }
    if (!pendingIdRef.current) {
      pendingIdRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `cm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    setSending(true);
    setError(null);
    const sent = await onSend(message, pendingIdRef.current, readyIds);
    setSending(false);
    if (sent) {
      pendingIdRef.current = '';
      setValue('');
      reset();
      return;
    }
    // Keep the text, the files and the id: the retry must be the same logical message.
    //
    // PA-23 — and say nothing here. The `onSend` owner renders the failure sentence
    // next to the control it owns; a second, differently-worded sentence from the
    // composer made one failure look like two problems. The composer's own `error`
    // state stays for uploads and screenshots, which nobody else owns.
  }, [onSend, readyIds, reset, uploading, value]);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      void submit();
    },
    [submit]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (canSend) {
          void submit();
        }
      }
    },
    [canSend, submit]
  );

  const handlePick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = '';
      void addFiles(files);
    },
    [addFiles]
  );

  const handleScreenshot = useCallback(async () => {
    const file = await capture();
    if (file) {
      await addFiles([file]);
    }
  }, [addFiles, capture]);

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (!allowAttachments) {
        return;
      }
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0) {
        return;
      }
      // Only swallow the paste when it actually carried an image, so pasting text
      // into the field still works.
      event.preventDefault();
      void addFiles(files);
    },
    [addFiles, allowAttachments]
  );

  let placeholder = basePlaceholder;
  if (unavailable) {
    placeholder = 'The Pronto Assistant is unavailable right now.';
  } else if (disabled) {
    placeholder = 'Pronto Assistant is working on this…';
  }

  const attachmentCount = attachments.length;
  const attachDisabled = !allowAttachments || blocked;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      data-testid="case-composer"
      onPaste={handlePaste}
    >
      <Stack spacing={1}>
        {unavailable ? (
          <Alert severity="info" sx={{ py: 0 }}>
            {health?.message ||
              'The Pronto Assistant is temporarily unavailable. Email support@prontohousing.com.'}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ py: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert
            severity="warning"
            sx={{ py: 0 }}
            onClose={clearNotice}
            data-testid="attachment-notice"
          >
            {notice}
          </Alert>
        ) : null}

        {captureError ? (
          <Alert
            severity="warning"
            sx={{ py: 0 }}
            onClose={clearError}
            data-testid="screenshot-error"
          >
            {captureError}
          </Alert>
        ) : null}

        {/* Not an error: the share picker was opened and dismissed. Quiet, and gone
            as soon as the user tries again. */}
        {captureNotice ? (
          <Alert
            severity="info"
            sx={{ py: 0 }}
            onClose={clearCaptureNotice}
            data-testid="screenshot-notice"
          >
            {captureNotice}
          </Alert>
        ) : null}

        <TextField
          inputRef={inputRef}
          value={value}
          onChange={(event) =>
            setValue(event.target.value.slice(0, MAX_LENGTH))
          }
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={blocked}
          multiline
          minRows={2}
          maxRows={6}
          size="small"
          fullWidth
          inputProps={{ 'aria-label': 'Message the Pronto Assistant' }}
        />

        <PendingAttachmentChips attachments={attachments} onRemove={remove} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              onChange={handlePick}
              hidden
              data-testid="attachment-input"
            />
            <Tooltip
              slotProps={PANEL_TOOLTIP_SLOT_PROPS}
              title={`Attach a document (up to ${MAX_FILES_PER_MESSAGE})`}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachDisabled}
                  aria-label="Attach a document"
                  data-testid="attach-file"
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              slotProps={PANEL_TOOLTIP_SLOT_PROPS}
              title="Take a screenshot"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => void handleScreenshot()}
                  disabled={attachDisabled || capturing}
                  aria-label="Take a screenshot"
                  data-testid="attach-screenshot"
                >
                  {capturing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ScreenshotIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {healthLoading
                ? 'Checking the Pronto Assistant…'
                : uploading
                ? `Uploading ${attachmentCount === 1 ? 'file' : 'files'}…`
                : `${value.length}/${MAX_LENGTH}`}
            </Typography>
          </Stack>
          <Button
            type="submit"
            variant="contained"
            color="info"
            size="small"
            disabled={!canSend}
            startIcon={
              sending ? (
                <CircularProgress size={14} />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
          >
            Send
          </Button>
        </Stack>

        {footer}
      </Stack>
    </Box>
  );
};

export default Composer;
